/**
 * SearchableSelect — a constrained-selection combobox (#792).
 *
 * Distinct from this directory's free-text `Combobox` (used by the
 * audit-action filter where typing an unknown value is a feature):
 * `SearchableSelect` only emits values that exist in `options`. The
 * trigger looks like a normal `Select` so it visually integrates with
 * the rest of an admin form; clicking opens a popover with a focused
 * search input, ↑/↓ to navigate the filtered list, Enter to commit,
 * Esc / outside-click / Tab-away to dismiss. Optional `groups` render
 * sticky-style headers above sub-lists when the option catalog is
 * large enough that grouping aids scanning (provider headers on the
 * model picker, primary motivating use case).
 *
 * Why not shadcn `Command + Popover` / `cmdk + @radix-ui/react-popover`:
 * the existing `Combobox` in this directory has an explicit anti-
 * pattern note ("a popover library / cmdk dependency for a small
 * bounded-purpose widget"). Two hand-rolled comboboxes — both built on
 * the same click-outside + ref pattern the Header app/variant dropdown
 * uses — are cheaper to maintain than two new dependencies and a new
 * primitive shape.
 *
 * Filter semantics:
 *   - Case-insensitive substring against the option's visible `label`,
 *     plus its optional hidden `searchText` (#2095).
 *   - A group header renders when AT LEAST one of its options matches.
 *     A model whose own label matches without its provider name also
 *     matches (e.g. typing "sonnet" matches `Claude 3.5 Sonnet` under
 *     the `anthropic` group even though "sonnet" isn't in "anthropic").
 *   - Maximum visible count caps the rendered list to keep the popover
 *     scannable; the search input is the operator's primary affordance
 *     once the catalog exceeds the cap.
 *
 * Picking without Enter (`pickSoleMatch`, #2890): opt-in. When the popover
 * closes by an outside click or Tab with text typed, and `soleMatch` finds
 * the one option that text names, that option is picked, so pressing a
 * Save beside the picker uses what was typed. Escape still dismisses
 * without picking. Off by default: the other screens using this picker
 * keep needing Enter or a click.
 *
 * Accessibility (WAI-ARIA combobox-with-listbox pattern):
 *   - Trigger button: `role="combobox"`, `aria-haspopup="listbox"`,
 *     `aria-expanded`, `aria-controls` (listbox id when open).
 *   - Search input: focused on open, `aria-controls` mirrors the
 *     listbox id, `aria-activedescendant` reflects the keyboard cursor.
 *   - Listbox: `role="listbox"`. Each option `role="option"` with
 *     `aria-selected` reflecting the current value match.
 *   - Group headers: `role="presentation"` (decorative); they don't
 *     enter the option enumeration assistive tech reads.
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface SearchableSelectOption {
  value: string
  label: string
  /** Optional grouping key. When set on at least one option, the
   * popover renders headers grouping options by this key in
   * `groups`-supplied order (or insertion order as a fallback). */
  group?: string
  /** Optional facet membership (#1844 Slice 1.5). The `facets` prop renders
   * a chip per key; an option shows under a facet when its `facets` include
   * that key. An option with NO `facets` field is facet-exempt — always
   * visible regardless of the active facet (e.g. a sentinel / orphan row).
   * Search always spans the full set, ignoring the active facet. */
  facets?: readonly string[]
  /** Optional adornment rendered at the RIGHT of the option row (#1844
   * Slice 2 — the cost-category pill, so cost is visible while browsing,
   * not only after selection). Purely decorative: it never affects search
   * (which matches `label`), grouping, or selection. Keep it compact — the
   * row is a tight `text-xs` line next to the (possibly long) label. */
  trailing?: ReactNode
  /** Renders the row visible but unpickable (#2095). Search still finds
   * it and it keeps its `trailing` adornment — the point is to answer
   * "why isn't this person in the list?" rather than leave the searcher
   * staring at an empty result. Keyboard navigation skips it and both
   * click and Enter refuse it, so it can never be committed. */
  disabled?: boolean
  /** Extra text search matches against, beyond the visible `label`
   * (#2095). Lets a row show one thing and be findable by another —
   * the person picker renders a name on the first line and the email
   * on the second via `trailing`, but an admin who only knows the
   * email address still has to be able to find them.
   *
   * Not rendered anywhere; supply only text the user could reasonably
   * type. */
  searchText?: string
}

/** A facet chip shown above the option list. Membership is declared per
 * option via `SearchableSelectOption.facets`; the component renders the chip
 * with a live count plus a built-in "All Models" chip that clears it. */
export interface SearchableSelectFacet {
  key: string
  label: string
  /** One line explaining what this chip is showing, rendered under the chip bar
   * while the chip is active (#1928).
   *
   * Exists because a chip that filters on *evidence* can be misread as filtering
   * on *fitness* — an operator seeing a short list needs to be told the others
   * are still available, not broken. A tooltip was the cheaper option and was
   * rejected: the misreading happens to someone who never hovers.
   *
   * Optional, so every existing facet renders exactly as before. */
  hint?: string
}

export interface SearchableSelectProps {
  /** Currently selected value (one of `options[i].value`, or `''`). */
  value: string
  /** Fired with the new value when the operator picks an option. */
  onChange: (value: string) => void
  options: readonly SearchableSelectOption[]
  /** Optional facet chips rendered above the option list (#1844 Slice 1.5).
   * Narrows browsing without confining search: typing in the search box
   * bypasses the active facet and spans the full option set. When provided,
   * a built-in "All Models" chip clears the filter. */
  facets?: readonly SearchableSelectFacet[]
  /** Facet key active on open (e.g. a curated shortlist); re-applied on
   * every open. Omit to open on "All Models". */
  defaultFacet?: string
  /** Optional group ordering. Headers render in this order; any group
   * referenced by an option but missing here falls through to the end
   * in first-seen order. Pass `[]` (or omit) to disable grouping. */
  groups?: readonly { key: string; label: string }[]
  /** Placeholder shown in the trigger when `value === ''`. */
  placeholder?: string
  /** Text shown in the search input. */
  searchPlaceholder?: string
  /** Disables the trigger. */
  disabled?: boolean
  /** Maximum options the popover renders at once. Defaults to 200 —
   * the pricing catalog can comfortably hold ~200 rows; beyond that
   * the operator should narrow the search. */
  maxOptions?: number
  /** Extra classes on the trigger button (e.g. inherited-styling
   * `italic text-muted-foreground` on the model picker rows). */
  className?: string
  /** Typography for the option rows. Defaults to `font-mono`, which
   * suits the model-id catalog this picker was built for. A picker
   * listing people's names passes a proportional class instead —
   * monospace names read as machine output, which is exactly the
   * impression #2094/#2095 are removing from these screens. */
  optionClassName?: string
  /** Forwarded as the trigger button's `id`. */
  id?: string
  /** A line under the options, for saying why some are missing (the model
   * picker names suppliers it cannot offer). Outside the listbox, so a
   * screen reader does not read it as an option. */
  footer?: ReactNode
  /** Forwarded as the trigger's accessible name when there's no
   * associated `<label htmlFor>`. */
  'aria-label'?: string
  /** Opt-in (#2890): closing the popover by an outside click or Tab picks
   * the one option the typed text names (see `soleMatch`), so no Enter is
   * needed. Escape never picks. */
  pickSoleMatch?: boolean
  /** With `pickSoleMatch`: told what was typed when the popover closes, and
   * how many options it matched; 1 when it picked one. Empty text and 0
   * when nothing was typed, so the caller can forget an earlier miss. */
  onSearchLeft?: (query: string, matchCount: number) => void
  /** A line shown under the options while text is typed, given the typed
   * text and the option it names (`soleMatch`), or null. */
  searchHint?: (query: string, sole: SearchableSelectOption | null) => ReactNode
}

/**
 * The one option typed text names (#2890), or null. A full value or
 * label, in any case, wins; otherwise exactly one pickable option must
 * contain the text, by the same rule the list filters with. A full name
 * wins first so a model that is also part of a longer name can still be
 * typed in full.
 */
export function soleMatch(
  query: string,
  options: readonly SearchableSelectOption[],
): SearchableSelectOption | null {
  const needle = query.trim().toLowerCase()
  if (!needle) return null
  const pickable = options.filter((o) => !o.disabled)
  const exact = pickable.find((o) => o.value.toLowerCase() === needle || o.label.toLowerCase() === needle)
  if (exact) return exact
  const matches = pickable.filter((o) => matchesQuery(o, needle))
  return matches.length === 1 ? matches[0]! : null
}

/** The list's filter rule, shared with `soleMatch` so the two never
 * disagree: case-insensitive substring on the label or the hidden
 * search text. `needle` is already trimmed and lower-cased. */
function matchesQuery(o: SearchableSelectOption, needle: string): boolean {
  return o.label.toLowerCase().includes(needle) || o.searchText?.toLowerCase().includes(needle) === true
}

export const SearchableSelect = forwardRef<HTMLButtonElement, SearchableSelectProps>(
  function SearchableSelect(
    {
      value,
      onChange,
      options,
      facets,
      defaultFacet,
      groups,
      placeholder = 'Select…',
      searchPlaceholder = 'Search…',
      disabled,
      maxOptions = 200,
      className,
      optionClassName,
      id,
      footer,
      'aria-label': ariaLabel,
      pickSoleMatch = false,
      onSearchLeft,
      searchHint,
    },
    ref,
  ) {
    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState('')
    const [cursor, setCursor] = useState(0)
    // #1844 Slice 1.5 — the active facet (null = "All Models"). Seeded on
    // open by the effect below, which falls back to All Models when the
    // default facet has no members.
    const [activeFacet, setActiveFacet] = useState<string | null>(null)
    const wrapperRef = useRef<HTMLDivElement>(null)
    const searchRef = useRef<HTMLInputElement>(null)
    const listboxId = useId()
    const optionIdBase = useId()

    // Leaving the popover by an outside click or Tab. With `pickSoleMatch`
    // (#2890), the one option the typed text names is picked first, and the
    // caller is told what was left typed. Read through a ref by the
    // outside-click listener, which is registered once per open and would
    // otherwise see the query as it was when the popover opened.
    const leave = () => {
      // Once per open: an outside press can be followed by the focus leaving
      // too, and the second must not report the same typing again.
      if (!openRef.current) return
      openRef.current = false
      if (pickSoleMatch) {
        const typed = query.trim()
        const sole = soleMatch(typed, options)
        if (sole) onChange(sole.value)
        const needle = typed.toLowerCase()
        onSearchLeft?.(
          typed,
          sole ? 1 : needle ? options.filter((o) => !o.disabled && matchesQuery(o, needle)).length : 0,
        )
      }
      setOpen(false)
    }
    const leaveRef = useRef(leave)
    leaveRef.current = leave
    const openRef = useRef(open)
    useEffect(() => {
      openRef.current = open
    }, [open])

    // Close on outside click. Matches the existing Combobox + Header
    // app/variant dropdown pattern — keeps the visual library narrow.
    useEffect(() => {
      if (!open) return
      function onMouseDown(e: MouseEvent) {
        if (
          wrapperRef.current &&
          !wrapperRef.current.contains(e.target as Node)
        ) {
          leaveRef.current()
        }
      }
      document.addEventListener('mousedown', onMouseDown)
      return () => document.removeEventListener('mousedown', onMouseDown)
    }, [open])

    // Focus the search input on open. Single rAF tick is enough — the
    // input renders in the same paint as `setOpen(true)`. Resetting
    // query + cursor here means a reopen always starts fresh, which
    // matches operator expectation for a fresh search.
    useEffect(() => {
      if (!open) return
      setQuery('')
      setCursor(0)
      // Seed the facet on open ONLY (not on later `options` changes, or a
      // user's facet click mid-session would be clobbered). Fall back to All
      // Models when the default facet has no members, so the picker never
      // opens empty (e.g. an install where no curated model is priced, or a
      // test catalog with none of the shortlist).
      const defaultHasMembers =
        defaultFacet != null && options.some((o) => o.facets?.includes(defaultFacet))
      setActiveFacet(defaultHasMembers ? defaultFacet : null)
      const id = requestAnimationFrame(() => searchRef.current?.focus())
      return () => cancelAnimationFrame(id)
      // eslint-disable-next-line react-hooks/exhaustive-deps -- `options` is
      // read at open-time on purpose; adding it would reset the facet on every
      // options change and defeat the user's manual facet selection.
    }, [open, defaultFacet])

    // Filter to the visible options. Precedence (matches the picker's
    // "search always reaches everything"):
    //   1. A non-empty search spans the FULL option set — the active facet
    //      is bypassed, so search is never confined to a facet subset.
    //   2. Otherwise the active facet narrows browsing; `null` (All Models),
    //      or no facets configured, means everything.
    //   3. A facet-exempt option (no `facets` field) is always visible, so
    //      sentinels/orphans never vanish behind a facet.
    const filtered = useMemo(() => {
      const needle = query.trim().toLowerCase()
      if (needle) {
        const matches = options.filter((o) => matchesQuery(o, needle))
        // A pasted full name (e.g. copied from the model catalog) should land
        // on that exact option, not on whichever longer name sorts first.
        // It also leaves its group: groups render in a fixed order, so only
        // the headerless bucket, which renders first, puts it at the top.
        const exact = matches.findIndex(o => o.value.toLowerCase() === needle)
        if (exact >= 0) {
          const [hit] = matches.splice(exact, 1)
          matches.unshift({ ...hit!, group: undefined })
        }
        return matches
      }
      if (!facets || facets.length === 0 || activeFacet == null) return options
      return options.filter((o) => o.facets === undefined || o.facets.includes(activeFacet))
    }, [options, query, facets, activeFacet])

    const visible = useMemo(
      () => (filtered.length > maxOptions ? filtered.slice(0, maxOptions) : filtered),
      [filtered, maxOptions],
    )

    // Per-facet counts (+ the All-Models total) for the chip labels —
    // computed over the FULL option set, independent of query/active facet.
    // "All Models" counts only facet-bearing options (catalog rows), so the
    // synthetic sentinels (cascade / orphan) don't inflate it.
    // Resolved once per render rather than inside the JSX, which looked it up
    // twice for one line of text.
    const activeFacetHint = useMemo(
      () => facets?.find((f) => f.key === activeFacet)?.hint,
      [facets, activeFacet],
    )
    const facetCounts = useMemo(() => {
      const counts = new Map<string, number>()
      if (facets) {
        for (const f of facets) {
          counts.set(
            f.key,
            options.reduce((n, o) => n + (o.facets?.includes(f.key) ? 1 : 0), 0),
          )
        }
      }
      return counts
    }, [facets, options])
    const allCount = useMemo(
      () => options.reduce((n, o) => n + (o.facets !== undefined ? 1 : 0), 0),
      [options],
    )

    // Bucket the visible options into ordered groups. Insertion order
    // for `groups`-specified keys first, then any extra group keys in
    // first-seen order; ungrouped options (no `group` field) land in an
    // anonymous bucket rendered with no header. This matches the model
    // picker's orphan-option case — it has no provider so it shouldn't
    // get one.
    const bucketed = useMemo(() => {
      // Grouping is opt-in via the `groups` prop, matching the prop
      // contract. When `groups` is omitted or empty, all options
      // render flat regardless of any `opt.group` field — a call site
      // that doesn't pass `groups` gets a single un-headered list,
      // not auto-bucketed headers. (Earlier the impl always grouped
      // when any option had `group` set, contradicting the doc.)
      if (!groups || groups.length === 0) {
        return visible.length > 0
          ? [{ groupKey: null, label: null, items: visible }]
          : []
      }

      const order: string[] = []
      const byKey = new Map<string, SearchableSelectOption[]>()
      const ungrouped: SearchableSelectOption[] = []
      if (groups && groups.length > 0) {
        for (const g of groups) {
          order.push(g.key)
          byKey.set(g.key, [])
        }
      }
      for (const opt of visible) {
        if (!opt.group) {
          ungrouped.push(opt)
          continue
        }
        let bucket = byKey.get(opt.group)
        if (!bucket) {
          bucket = []
          byKey.set(opt.group, bucket)
          order.push(opt.group)
        }
        bucket.push(opt)
      }
      const out: Array<{ groupKey: string | null; label: string | null; items: SearchableSelectOption[] }> = []
      if (ungrouped.length > 0) out.push({ groupKey: null, label: null, items: ungrouped })
      for (const key of order) {
        const items = byKey.get(key) ?? []
        if (items.length === 0) continue
        const groupDef = groups?.find((g) => g.key === key)
        out.push({ groupKey: key, label: groupDef?.label ?? key, items })
      }
      return out
    }, [visible, groups])

    // The flat order used by ↑/↓ navigation. Group headers don't enter
    // this order; the cursor only lands on selectable options. Disabled
    // options are excluded for the same reason (#2095) — they render,
    // but arrowing onto one and pressing Enter must not commit it.
    const flatOrder = useMemo(
      () => bucketed.flatMap((b) => b.items.filter((o) => !o.disabled)),
      [bucketed],
    )

    // Reset the cursor to a valid index when the filtered result set
    // changes (e.g. typing a new character). Preserves the cursor when
    // it's still in range, snaps to 0 otherwise. Without this the
    // cursor can point past the end and Enter would be a no-op.
    useEffect(() => {
      if (cursor >= flatOrder.length) setCursor(0)
    }, [flatOrder.length, cursor])

    const selectedLabel = useMemo(
      () => options.find((o) => o.value === value)?.label ?? '',
      [options, value],
    )

    const commit = useCallback(
      (next: string) => {
        // Marked left first, so the focus moving away after this pick cannot
        // pick again from what was typed.
        openRef.current = false
        onChange(next)
        // A pick from the list resolves whatever was typed.
        if (pickSoleMatch) onSearchLeft?.('', 0)
        setOpen(false)
      },
      [onChange, pickSoleMatch, onSearchLeft],
    )

    function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setCursor((c) => (flatOrder.length === 0 ? 0 : (c + 1) % flatOrder.length))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setCursor((c) =>
          flatOrder.length === 0 ? 0 : (c - 1 + flatOrder.length) % flatOrder.length,
        )
      } else if (e.key === 'Enter') {
        e.preventDefault()
        const picked = flatOrder[cursor]
        if (picked) commit(picked.value)
      } else if (e.key === 'Escape') {
        e.preventDefault()
        // Dismisses without picking, and drops what was typed.
        openRef.current = false
        if (pickSoleMatch) onSearchLeft?.('', 0)
        setOpen(false)
      } else if (e.key === 'Tab') {
        // With a facet bar, let Tab move focus into the chips (the next
        // focusable elements after the search input) so they're
        // keyboard-reachable; without facets, Tab dismisses as before (no
        // focus trap either way).
        if (!facets || facets.length === 0) leave()
      }
    }

    const triggerLabel = value && selectedLabel ? selectedLabel : placeholder
    const showPlaceholder = !value || !selectedLabel

    return (
      <div
        ref={wrapperRef}
        className="relative"
        // With `pickSoleMatch`, focus leaving the whole picker is leaving the
        // list too (#2890). With facets, Tab moves into the chips instead of
        // closing, so a keyboard user who tabs on past them to a Save would
        // otherwise skip the pick. Moving between the picker's own parts does
        // not count. Without the setting, nothing changes.
        onBlur={(e) => {
          if (!pickSoleMatch) return
          const next = e.relatedTarget as Node | null
          if (next && wrapperRef.current?.contains(next)) return
          leaveRef.current()
        }}
      >
        <button
          ref={ref}
          type="button"
          id={id}
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listboxId : undefined}
          aria-label={ariaLabel}
          disabled={disabled}
          onClick={() => {
            if (!disabled) setOpen((o) => !o)
          }}
          // Open on ArrowDown/Enter/Space when the trigger has focus —
          // matches native <select> behavior so keyboard-only operators
          // can reach the popover without clicking.
          onKeyDown={(e) => {
            if (disabled) return
            if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              setOpen(true)
            }
          }}
          // Match the visual weight of `Select` — same height, border,
          // focus ring, chevron — so the picker doesn't jump out next
          // to a sibling Select on the same form.
          className={cn(
            'flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs transition-colors focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
            className,
          )}
        >
          <span
            className={cn(
              'truncate text-left',
              showPlaceholder && 'text-muted-foreground',
            )}
          >
            {triggerLabel}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
        </button>
        {open && (
          // The popover. Absolutely positioned beneath the trigger,
          // z-50 so it floats over sibling form rows.
          <div className="absolute z-50 mt-1 w-full min-w-[16rem] rounded-md border bg-popover text-popover-foreground shadow-md">
            <div className="border-b p-2">
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value)
                  setCursor(0)
                }}
                onKeyDown={onKeyDown}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                aria-controls={listboxId}
                aria-activedescendant={
                  flatOrder[cursor] ? `${optionIdBase}-${cursor}` : undefined
                }
                autoComplete="off"
                className="h-8 w-full rounded-sm border border-input bg-transparent px-2 text-sm focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring"
              />
            </div>
            {facets && facets.length > 0 && (
              // Facet bar (#1844 Slice 1.5). Chips are real buttons with
              // `onClick`, so they're operable by keyboard (Enter/Space on a
              // focused chip) as well as mouse — and Tab from the search input
              // moves focus INTO the bar (see the Tab branch of onKeyDown)
              // rather than dismissing, so a keyboard-only operator can reach
              // them. Each handler refocuses the search input so the operator
              // keeps typing after filtering. The popover stays open because a
              // chip lives inside the wrapper, so the close-on-outside-click
              // handler never fires. Typing bypasses the facet (see `filtered`).
              <div
                role="group"
                aria-label="Filter options"
                className="flex flex-wrap gap-1 border-b p-2"
              >
                {facets.map((f) => {
                  const on = activeFacet === f.key
                  return (
                    <button
                      key={f.key}
                      type="button"
                      aria-pressed={on}
                      onClick={() => {
                        setActiveFacet(f.key)
                        setCursor(0)
                        searchRef.current?.focus()
                      }}
                      className={cn(
                        'rounded-full border px-2 py-0.5 text-xs transition-colors',
                        on
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-input text-muted-foreground hover:bg-muted/60',
                      )}
                    >
                      {f.label}
                      <span className="ml-1 opacity-70">{facetCounts.get(f.key) ?? 0}</span>
                    </button>
                  )
                })}
                <button
                  type="button"
                  aria-pressed={activeFacet == null}
                  onClick={() => {
                    setActiveFacet(null)
                    setCursor(0)
                    searchRef.current?.focus()
                  }}
                  className={cn(
                    'rounded-full border px-2 py-0.5 text-xs transition-colors',
                    activeFacet == null
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-input text-muted-foreground hover:bg-muted/60',
                  )}
                >
                  All Models
                  <span className="ml-1 opacity-70">{allCount}</span>
                </button>
                {/* Hint for the active chip, on its own row below the chips so
                    it never reflows the bar. Only the active facet's hint shows
                    — all of them at once would be a wall of text explaining
                    filters the operator is not using. */}
                {activeFacetHint && (
                  <p className="w-full pt-1 text-[11px] leading-snug text-muted-foreground">
                    {activeFacetHint}
                  </p>
                )}
              </div>
            )}
            <ul
              id={listboxId}
              role="listbox"
              className="max-h-64 overflow-y-auto py-1 text-sm"
            >
              {flatOrder.length === 0 && (
                <li className="px-3 py-2 text-xs text-muted-foreground" role="presentation">
                  No matches.
                </li>
              )}
              {bucketed.map((bucket) => (
                <GroupBlock
                  key={bucket.groupKey ?? '_ungrouped'}
                  label={bucket.label}
                  items={bucket.items}
                  flatOrder={flatOrder}
                  optionIdBase={optionIdBase}
                  cursor={cursor}
                  optionClassName={optionClassName}
                  setCursor={setCursor}
                  value={value}
                  onPick={commit}
                />
              ))}
              {filtered.length > maxOptions && (
                <li
                  className="border-t mt-1 px-3 py-1.5 text-[10px] text-muted-foreground"
                  aria-hidden="true"
                >
                  Showing first {maxOptions} of {filtered.length} matches — narrow your search to see more.
                </li>
              )}
            </ul>
            {searchHint && query.trim() !== '' && (() => {
              const hint = searchHint(query.trim(), soleMatch(query, options))
              return hint ? (
                <div className="border-t px-3 py-1.5 text-xs text-muted-foreground" role="status" data-testid="search-hint">
                  {hint}
                </div>
              ) : null
            })()}
            {footer && (
              <div className="border-t px-3 py-1.5 text-xs text-muted-foreground">{footer}</div>
            )}
          </div>
        )}
      </div>
    )
  },
)

/** One bucket (header + its options) inside the popover. Extracted so
 * the parent's render stays scannable; no behavior lives here. */
function GroupBlock({
  label,
  items,
  flatOrder,
  optionIdBase,
  cursor,
  setCursor,
  value,
  onPick,
  optionClassName,
}: {
  label: string | null
  items: readonly SearchableSelectOption[]
  flatOrder: readonly SearchableSelectOption[]
  optionIdBase: string
  cursor: number
  setCursor: (i: number) => void
  value: string
  onPick: (next: string) => void
  optionClassName?: string
}) {
  return (
    <>
      {label && (
        <li
          // `role="presentation"` keeps the header out of the listbox's
          // option enumeration for assistive tech — it's visual chrome,
          // not a selectable item.
          role="presentation"
          className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wide text-muted-foreground"
        >
          {label}
        </li>
      )}
      {items.map((opt) => {
        // Disabled options are absent from `flatOrder`, so `indexOf`
        // returns -1 — they get no option id and can never be the
        // `aria-activedescendant` target (#2095).
        const flatIndex = flatOrder.indexOf(opt)
        const isActive = flatIndex >= 0 && flatIndex === cursor
        const isSelected = opt.value === value
        return (
          <li
            key={opt.value}
            id={flatIndex >= 0 ? `${optionIdBase}-${flatIndex}` : undefined}
            role="option"
            aria-selected={isSelected}
            aria-disabled={opt.disabled || undefined}
          >
            <button
              type="button"
              tabIndex={-1}
              disabled={opt.disabled}
              // `onMouseDown` (not `onClick`) fires BEFORE the search
              // input's blur — selecting an option doesn't race with
              // the close-on-outside-click handler.
              onMouseDown={(e) => {
                e.preventDefault()
                if (opt.disabled) return
                onPick(opt.value)
              }}
              onMouseEnter={() => {
                if (flatIndex >= 0) setCursor(flatIndex)
              }}
              className={cn(
                'flex w-full flex-col items-start gap-0.5 px-3 py-1.5 text-left text-xs',
                optionClassName ?? 'font-mono',
                isActive && 'bg-muted/60',
                isSelected && !isActive && 'bg-muted/30',
                isSelected && 'font-semibold',
                opt.disabled && 'cursor-not-allowed opacity-50',
              )}
            >
              {/* The option's NAME owns its own line.
                *
                * This used to be one row — label, then the adornment pushed
                * right with `shrink-0` and a comment explaining that the label
                * should truncate rather than squash the adornment. That was
                * right when the adornment was a single small cost pill (#1844).
                * #1928 added three more (tool calling, reasoning, context), and
                * the priority inverted: on a ~400px picker the adornments took
                * ~320px and the name rendered as `o…`, which is unusable when
                * the name is the thing you are searching for.
                *
                * So the name is never in competition with its own metadata.
                * `w-full` + `truncate` means it still truncates on a genuinely
                * long name, but only after using the whole row. */}
              <span className="w-full truncate">{opt.label}</span>
              {opt.trailing != null && (
                // Second line. `flex-wrap` so a narrow picker stacks the
                // adornments rather than clipping them.
                <span className="flex w-full flex-wrap items-center gap-1">
                  {opt.trailing}
                </span>
              )}
            </button>
          </li>
        )
      })}
    </>
  )
}
